/**
 * SQC-562 / API-1303: MidFirst Bank Servicing Transfer Report Generation
 * Generates 9 legacy servicing transfer files for MidFirst Bank.
 * API-1303: Added loan number list input; loan_type is now an optional secondary filter.
 *
 * @author teman.beck (SQC-562, API-1303)
 */
import { useState, useCallback, FormEvent, useRef } from 'react';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { useAuthContext } from '../contexts/AuthContext';
import { useViewingContext } from '../contexts/ViewingContext';
import { generateMidFirstTransfer, MidFirstTransferResponse } from '../common/queryFunctions';
import DefaultModal from '../components/modals/DefaultModal';
import EnvelopeWithBox from '../components/EnvelopeWithBox';
import { borrowerNoAccessMsg } from '../common/constants';
import css from '../css/MidFirstTransferPage.module.css';

interface FormInput {
  transfer_date: Dayjs | null;
  loan_numbers_raw: string;
  loan_type: string;
  upload_to_imaging: boolean;
}

const LOAN_TYPE_OPTIONS = [
  { value: 'ALL',  label: 'All Loan Types' },
  { value: 'FHA',  label: 'FHA' },
  { value: 'VA',   label: 'VA' },
  { value: 'USDA', label: 'USDA' },
];

const DEFAULT_FORM: FormInput = {
  transfer_date: null,
  loan_numbers_raw: '',
  loan_type: 'ALL',
  upload_to_imaging: true,
};

/** Parse textarea input into a clean array — supports newline or comma separated. */
const parseLoanNumbers = (raw: string): string[] =>
  raw.split(/[\n,\s]+/).map(s => s.trim()).filter(s => s.length > 0);

const MidFirstTransferPage: React.FC = () => {
  const { token } = useAuthContext();
  const { isServicerView } = useViewingContext();
  const abortControllerRef = useRef<AbortController | null>(null);

  const [formInput, setFormInput] = useState<FormInput>(DEFAULT_FORM);

  const parsedLoanNumbers = parseLoanNumbers(formInput.loan_numbers_raw);

  const mutation: UseMutationResult<MidFirstTransferResponse, Error, FormInput> = useMutation({
    mutationFn: (data: FormInput) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      return generateMidFirstTransfer(controller.signal, token, {
        transfer_date: data.transfer_date!.format('YYYYMMDD'),
        loan_numbers: parseLoanNumbers(data.loan_numbers_raw),
        loan_type: data.loan_type,
        upload_to_imaging: data.upload_to_imaging,
      });
    },
  });

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormInput(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!formInput.transfer_date || !formInput.transfer_date.isValid()) {
        alert('A valid transfer date is required.');
        return;
      }
      if (parsedLoanNumbers.length === 0) {
        alert('At least one loan number is required.');
        return;
      }
      mutation.reset();
      mutation.mutate(formInput);
    },
    [formInput, parsedLoanNumbers, mutation],
  );

  const handleReset = useCallback(() => {
    mutation.reset();
    setFormInput(DEFAULT_FORM);
  }, [mutation]);

  const isSubmitDisabled =
    mutation.status === 'pending' ||
    !formInput.transfer_date ||
    !formInput.transfer_date.isValid() ||
    parsedLoanNumbers.length === 0;

  const StatusModal = () => {
    const isPending = mutation.status === 'pending';
    const isError   = mutation.status === 'error';
    const isSuccess = mutation.status === 'success';

    if (!isPending && !isError && !isSuccess) return null;

    const title = isPending ? 'Generating Files...' : isError ? 'Error' : 'Complete';

    return (
      <DefaultModal
        open={true}
        className={css.modalContainer}
        title={<div className={css.modalTitle}>{title}</div>}
        content={
          <div className={css.modalContent}>
            {isPending && <div>Generating 9 MidFirst transfer files. This may take a moment...</div>}
            {isError && <div className={css.errorText}>{mutation.error.message}</div>}
            {isSuccess && (
              <>
                <div className={css.summaryRow}>
                  <span>Download started for:</span>
                  <strong>{mutation.data.zipFileName}</strong>
                </div>
                <div className={css.summaryRow}>
                  <span>Files in zip:</span>
                  <strong>{mutation.data.filesGenerated}</strong>
                </div>
                <div className={css.summaryRow}>
                  <span>Loans submitted:</span>
                  <strong>{parsedLoanNumbers.length}</strong>
                </div>
                <div className={css.summaryRow}>
                  <span>Transfer Date:</span>
                  <strong>{formInput.transfer_date?.format('MM/DD/YYYY')}</strong>
                </div>
                <div className={css.summaryRow}>
                  <span>Loan Type Filter:</span>
                  <strong>{formInput.loan_type}</strong>
                </div>
              </>
            )}
          </div>
        }
        footer={
          (isError || isSuccess) && (
            <div className={css.modalFooter}>
              <button className={css.modalButton} onClick={handleReset}>
                OK
              </button>
            </div>
          )
        }
      />
    );
  };

  if (!isServicerView) return <div className='access-error-msg'>{borrowerNoAccessMsg}</div>;

  return (
    <EnvelopeWithBox blockName='MidFirst Transfer'>
      <form className={css.form} onSubmit={handleSubmit}>

        <div className={css.fieldGroup}>
          <label htmlFor='transfer_date'>Transfer Date</label>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={formInput.transfer_date}
              onChange={(val: Dayjs | null) => setFormInput(prev => ({ ...prev, transfer_date: val }))}
              slotProps={{ textField: { size: 'small', className: css.input } }}
            />
          </LocalizationProvider>
        </div>

        <div className={css.fieldGroup}>
          <label htmlFor='loan_numbers_raw'>
            Loan Numbers
            <span className={css.fieldHint}> — one per line or comma separated</span>
          </label>
          <textarea
            id='loan_numbers_raw'
            name='loan_numbers_raw'
            value={formInput.loan_numbers_raw}
            onChange={handleChange}
            className={css.textarea}
            rows={6}
            placeholder={'115071029100\n115076034700\n124221000200'}
          />
          {parsedLoanNumbers.length > 0 && (
            <span className={css.fieldHint}>{parsedLoanNumbers.length} loan{parsedLoanNumbers.length !== 1 ? 's' : ''} entered</span>
          )}
        </div>

        <div className={css.fieldGroup}>
          <label htmlFor='loan_type'>
            Loan Type Filter
            <span className={css.fieldHint}> — optional</span>
          </label>
          <select
            id='loan_type'
            name='loan_type'
            value={formInput.loan_type}
            onChange={handleChange}
            className={css.select}
          >
            {LOAN_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className={css.checkboxGroup}>
          <label>
            <input
              type='checkbox'
              name='upload_to_imaging'
              checked={formInput.upload_to_imaging}
              onChange={handleChange}
            />
            Upload to Imaging
          </label>
        </div>

        <button
          type='submit'
          className={css.submitButton}
          disabled={isSubmitDisabled}
        >
          {mutation.status === 'pending' ? 'Generating...' : 'Generate Transfer Files'}
        </button>
      </form>
      {StatusModal()}
    </EnvelopeWithBox>
  );
};

export default MidFirstTransferPage;
